import type { V2 } from '../../core/math';
import { Rng } from '../../core/rng';
import type { Actor } from '../../sim/actor';
import type { ClassDef, IdentityController } from '../../sim/registry';
import { scaleShape } from '../../sim/shapes';
import { type Mod, T, forEachHit, tripod } from '../../sim/skills';
import type { AreaSpec, HitSpec, ResolvedSkill, SkillAction, SkillDef, StatusApply } from '../../sim/skillTypes';
import { registerStatus } from '../../sim/status';
import { applyStatus, removeStatus } from '../../sim/statusSystem';
import { GRADE_RANK, type StaggerTier, stepStagger } from '../../sim/types';
import type { World } from '../../sim/world';
import { classBaseStats } from '../balance';
import { area, basicAttack, circle, dashSkill, hit, identityOp, invuln, proj, rect, self, skill, stage, teleport } from './kit';

/**
 * STORMWEAVER — elemental battle-mage (caster with a burst window + teleport).
 * Layers fire, frost and lightning. Every spell builds Arcana; at 30+ the
 * caster Overflows into a rapid-fire window (at 100: Arcane Torrent), and can
 * spend Arcana on Phase Step blinks.
 */

const HUE = 0x6aa8ff;
const FIRE = 0xff7a3a;
const STORM = 0xa9c4ff;
const FROST = 0x7fe0ff;

const OVERFLOW_MIN = 30;
const PHASE_MIN = 30;
const PHASE_COST = 15;
const DRAIN = 8;
const TORRENT_DRAIN = 5;
/** Z internal cooldown (activation and early stop). */
const INTERNAL_CD = 2;
/** Fraction of every current cooldown removed when Overflow starts. */
const OVERFLOW_CDR = 0.25;
const ELEMENTAL_BONUS = 0.15;
const SURGE_BONUS = 0.2;
/** Cast-bar spells grant +50% Arcana. */
const CASTING_GAIN = 1.5;
const BASIC_GAIN = 2;

registerStatus({ id: 'sw_overflow', name: 'Overflow', kind: 'buff', icon: 'sw_overflow', description: 'Cast times -50%; elemental damage +15%; fire spells Weak Point +1; lightning spells stagger +1 tier.', stacking: 'refresh', mods: { castSpeedPct: 1 }, color: 0x6aa8ff });
registerStatus({ id: 'sw_torrent', name: 'Arcane Torrent', kind: 'buff', icon: 'sw_torrent', description: 'Crit rate +20%; Arcana drains more slowly.', stacking: 'refresh', mods: { critRate: 0.2 }, color: 0xc8e4ff });

const BURN = (duration: number, value = 0.3): StatusApply => ({ id: 'burn', duration, value });
const CHILL = (duration = 3): StatusApply => ({ id: 'chill', duration });
const SHOCK: StatusApply = { id: 'shock', duration: 4, value: 0.15 };
/** Lingering fields tick for damage only: no stagger or destruction per tick. */
const FIELD: Partial<HitSpec> = { stagger: 'none', weakPoint: 0 };

// ------------------------------------------------------------------ skills

const emberLine = skill({
  id: 'sw_ember', name: 'Ember Line', hue: FIRE, icon: 'lance', element: 'fire', identityGain: 6,
  description: 'Sear a line of fire 8 m ahead. Foes are paralysed and burn for 5 s.',
  castType: 'normal', cooldown: 10, mana: 70,
  stages: [stage('Shoot', 0.5, [hit(0.18, rect(8, 2.5), 3.5, { status: [BURN(5)], reaction: { kind: 'flinch' } })])],
});

const stormOrb = skill({
  id: 'sw_orb', name: 'Storm Orb', hue: STORM, icon: 'orb', element: 'lightning', identityGain: 6,
  description: 'Hurl a crackling orb to a point (up to 12 m). It hovers for 3 s, striking 6 times and drawing foes toward its centre.',
  castType: 'point', cooldown: 12, mana: 80, maxRange: 12, stagger: 'low',
  stages: [stage('Cast', 0.45, [area(0.15, { shape: circle(3.5), anchor: 'point', delay: 0.15, duration: 2.75, tickInterval: 0.5, pull: 2.5, telegraph: 'none', hit: { shape: circle(3.5), coef: 0.7, reaction: { kind: 'flinch' } } })])],
});

const frostNova = skill({
  id: 'sw_nova', name: 'Frost Nova', hue: FROST, icon: 'shockwave', element: 'water', identityGain: 6,
  description: 'Release a burst of frost around you (4.5 m) that chills foes for 3 s (attack speed -20%, move speed -40%). Counters blue-glowing enemies.',
  castType: 'normal', cooldown: 11, mana: 75, positional: 'back', superArmor: 'paralysis', counter: true, stagger: 'mid',
  stages: [stage('Slam', 0.55, [hit(0.22, circle(4.5), 5.0, { status: [CHILL(3)], reaction: { kind: 'flinch' } })])],
});

const pillarOfFlame = skill({
  id: 'sw_pillar', name: 'Pillar of Flame', hue: FIRE, icon: 'impact', element: 'fire', identityGain: 10,
  description: 'Call a column of fire at a point (up to 14 m) that launches foes.',
  castType: 'point', cooldown: 14, mana: 85, maxRange: 14, stagger: 'mid', weakPoint: 1,
  stages: [stage('Cast', 0.5, [area(0.15, { shape: circle(3), anchor: 'point', delay: 0.55, telegraph: 'none', vfx: 'pillar', hit: { shape: circle(3), coef: 9, reaction: { kind: 'launch', power: 0.3, duration: 0.9 } } })])],
});

const arcBeam = skill({
  id: 'sw_arc', name: 'Arc Beam', hue: STORM, element: 'lightning', identityGain: 14,
  description: 'Hold to channel a 16 m lightning beam for up to 2 s, striking 5 times. The beam follows the cursor.',
  castType: 'holding', cooldown: 24, mana: 110, superArmor: 'paralysis', stagger: 'high',
  stages: [],
  holding: { maxHold: 2, tickInterval: 0.4, anim: 'Channel', tick: [hit(0, rect(16, 1.5), 2.0, { reaction: { kind: 'flinch' } })] },
});

/** Where the six lances land around the aim point (forward = z). */
const LANCE_OFFSETS: V2[] = [
  { x: 0, z: 0 },
  { x: 1.3, z: 0.6 },
  { x: -1.2, z: 0.9 },
  { x: 0.4, z: -1.4 },
  { x: -1.1, z: -0.8 },
  { x: 0.9, z: 1.5 },
];
const LANCE_RADIUS = 2.6;

const glacialLances = skill({
  id: 'sw_lances', name: 'Glacial Lances', hue: FROST, icon: 'claw', element: 'water', identityGain: 10,
  description: 'Rain six ice lances around a point (up to 14 m), covering a 4 m radius.',
  castType: 'point', cooldown: 22, mana: 105, maxRange: 14, superArmor: 'paralysis', stagger: 'midHigh',
  stages: [
    stage(
      'Cast',
      0.55,
      LANCE_OFFSETS.map((offset, i) =>
        area(0.12, { shape: circle(LANCE_RADIUS), anchor: 'point', offset, delay: 0.25 + i * 0.08, telegraph: 'none', hit: { shape: circle(LANCE_RADIUS), coef: 11 / 6, stagger: 'low', reaction: { kind: 'flinch' } } }),
      ),
    ),
  ],
});

const thunderclap = skill({
  id: 'sw_thunder', name: 'Thunderclap', hue: STORM, icon: 'bolt', element: 'lightning', identityGain: 14,
  description: 'Channel for 1.2 s, then split the sky with a thunderbolt on a point (up to 16 m, 5 m radius) that knocks foes down.',
  castType: 'casting', castTime: 1.2, cooldown: 28, mana: 120, maxRange: 16, superArmor: 'paralysis', stagger: 'high',
  stages: [stage('Slam', 0.45, [area(0.05, { shape: circle(5), anchor: 'point', delay: 0.1, telegraph: 'none', vfx: 'pillar', hit: { shape: circle(5), coef: 19, reaction: { kind: 'knockdown', duration: 1.2 } } })])],
});

const starfall = skill({
  id: 'sw_starfall', name: 'Starfall', hue: FIRE, icon: 'meteor', element: 'fire', identityGain: 14,
  description: 'Channel for 1 s, then bring a meteor down on a point (up to 14 m, 4.5 m radius).',
  castType: 'casting', castTime: 1.0, cooldown: 30, mana: 125, maxRange: 14, superArmor: 'paralysis', stagger: 'midHigh', weakPoint: 2,
  stages: [stage('Cast', 0.5, [area(0.05, { shape: circle(4.5), anchor: 'point', delay: 0.4, telegraph: 'none', vfx: 'meteor', hit: { shape: circle(4.5), coef: 22, reaction: { kind: 'knockdown', duration: 1.2, power: 1 } } })])],
});

/**
 * Twelve meteors scattered (deterministically, so every peer replays the same
 * storm) around the target: one on it, an inner ring of four within 4.5 m and
 * seven wide strikes out to ~8.5 m, so the storm covers ~12 m.
 */
const SKYREND_OFFSETS: V2[] = (() => {
  const rng = new Rng('skyrend-tempest');
  const out: V2[] = [{ x: 0, z: 0 }];
  for (let i = 1; i < 12; i++) {
    const r = i <= 4 ? 2.5 + 2 * rng.next() : 6 + 2.5 * rng.next();
    const ang = i * 2.39996 + (rng.next() - 0.5) * 0.6; // golden-angle spread, jittered
    out.push({ x: Math.sin(ang) * r, z: Math.cos(ang) * r });
  }
  return out;
})();

const skyrendTempest = skill({
  id: 'sw_skyrend', name: 'Skyrend Tempest', hue: 0xff8a3a, element: 'fire', kind: 'awakening',
  description: 'APEX ART. Tear the sky open above a point (up to 14 m): for 6 s, twelve meteors crash down around it (4 m each). Triple damage to normal and elite foes.',
  castType: 'point', cooldown: 300, mana: 0, maxRange: 14, superArmor: 'full', stagger: 'high', weakPoint: 2, tags: ['vsMobs:2'],
  stages: [
    stage('Cast', 1.0, [
      invuln(0, 1.0),
      self(0.1),
      ...SKYREND_OFFSETS.map((offset, i) =>
        area(0.3, { shape: circle(4), anchor: 'point', offset, delay: 0.3 + i * 0.5, telegraph: 'none', vfx: 'meteor', hit: { shape: circle(4), coef: 3, stagger: 'low', weakPoint: 1, reaction: { kind: 'knockdown', duration: 1 } } }),
      ),
    ]),
  ],
});

// Identity skills (Z/X).
const overflowSkill = skill({
  id: 'sw_overflow', name: 'Overflow', hue: 0x9ad0ff, icon: 'buff', kind: 'identity',
  description: 'Needs 30 Arcana. Pour your Arcana out (it drains 8/s): cast times -50%, current cooldowns -25%, elemental spells +15% damage, fire spells Weak Point +1, lightning spells stagger +1 tier. At 100 Arcana it becomes Arcane Torrent: +20% crit rate and a 5/s drain. Press Z again to stop.',
  castType: 'normal', cooldown: INTERNAL_CD, mana: 0, superArmor: 'push',
  stages: [stage('Buff', 0.3, [self(0.02), identityOp(0.05, 'overflow')], { cancelAt: 0.1 })],
});

const phaseStep = skill({
  id: 'sw_phase', name: 'Phase Step', hue: 0x9ad0ff, icon: 'dash', kind: 'identity',
  description: 'Needs 30 Arcana; costs 15. Blink to the cursor (up to 12 m), passing through anything, briefly invulnerable.',
  castType: 'point', cooldown: 1, mana: 0, maxRange: 12, superArmor: 'full',
  stages: [stage('Dash', 0.22, [invuln(0, 0.2), teleport(0.03, 12, true)], { cancelAt: 0.08 })],
});

// ------------------------------------------------------------------ tripod helpers

function eachAction(s: ResolvedSkill, fn: (a: SkillAction) => void): void {
  for (const st of s.stages) st.actions.forEach(fn);
  s.holding?.finisher?.actions.forEach(fn);
  s.holding?.tick.forEach(fn);
}

function eachArea(s: ResolvedSkill, fn: (a: AreaSpec) => void): void {
  eachAction(s, (a) => {
    if (a.kind === 'area') fn(a.area);
  });
}

/** First area action of the first stage (the skill's main ground effect). */
function mainArea(s: ResolvedSkill): Extract<SkillAction, { kind: 'area' }> | undefined {
  return s.stages[0]?.actions.find((a): a is Extract<SkillAction, { kind: 'area' }> => a.kind === 'area');
}

/** Radius of the main area relative to its authored radius (keeps "Area +X%" facets). */
function areaScale(s: ResolvedSkill, authored: number): number {
  const sh = mainArea(s)?.area.hit.shape;
  return sh?.kind === 'circle' ? sh.radius / authored : 1;
}

/** "Area +X%" that also scales ground-effect telegraphs, not just their hit shapes. */
const aoeAll = (pct: number): Mod => (s) => {
  T.aoe(pct)(s);
  const k = 1 + pct;
  eachArea(s, (ar) => {
    ar.shape = ar.shape.kind === 'rect' ? { ...ar.shape, width: ar.shape.width * k } : scaleShape(ar.shape, k);
  });
};

/** Shifts every ground effect's eruption delay (clamped to a minimum). */
const shiftDelay = (dt: number, min = 0.05): Mod => (s) => {
  eachArea(s, (ar) => {
    ar.delay = Math.max(min, (ar.delay ?? 0) + dt);
  });
};

/** Rewrites a status (by id) on every hit and on facet-added statuses. Never mutates shared objects. */
const editStatus = (id: string, fn: (st: StatusApply) => StatusApply): Mod => (s) => {
  forEachHit(s, (h) => {
    if (h.status) h.status = h.status.map((st) => (st.id === id ? fn(st) : st));
  });
  s.extraStatus = s.extraStatus.map((st) => (st.id === id ? fn(st) : st));
};

const addTag = (t: string): Mod => (s) => {
  s.tags = [...(s.tags ?? []), t];
};

const fireWall = (s: ResolvedSkill): void => {
  const st = s.stages[0];
  if (!st) return;
  for (const a of st.actions) if (a.kind === 'hit' && a.hit.shape.kind === 'rect') a.hit.shape = { ...a.hit.shape, width: a.hit.shape.width * 2 };
  const wall = rect(8, 5);
  st.actions.push(area(0.2, { shape: wall, anchor: 'self', delay: 0.1, duration: 2.75, tickInterval: 0.5, telegraph: 'none', hit: { shape: wall, coef: (3.5 * 0.7) / 6, ...FIELD } }));
};

const sweepingBlaze = (s: ResolvedSkill): void => {
  s.castType = 'point';
  s.maxRange = 12;
  for (const st of s.stages) {
    for (const a of st.actions) {
      if (a.kind === 'hit') a.hit = { ...a.hit, anchor: 'point', shape: rect(8, 2.5, -4, 90), reaction: { kind: 'launch', power: 0.3, duration: 0.9 } };
    }
  }
};

const stormSpire = (s: ResolvedSkill): void => {
  const k = 5 / 3.5;
  eachArea(s, (ar) => {
    ar.shape = scaleShape(ar.shape, k);
    ar.hit = { ...ar.hit, shape: scaleShape(ar.hit.shape, k) };
    ar.pull = 5;
  });
};

const shatterfrost = (s: ResolvedSkill): void => {
  const st = s.stages[0];
  const nova = st?.actions.find((a): a is Extract<SkillAction, { kind: 'hit' }> => a.kind === 'hit');
  if (!st || !nova) return;
  const shape = nova.hit.shape;
  st.actions.push(area(nova.at, { shape, anchor: 'self', delay: 1, telegraph: 'none', hit: { shape, coef: nova.hit.coef * 0.6, status: [{ id: 'freeze', duration: 3 }] } }));
};

const emberPool = (s: ResolvedSkill): void => {
  const st = s.stages[0];
  const pillar = mainArea(s);
  if (!st || !pillar) return;
  const p = pillar.area;
  st.actions.push(area(pillar.at, { shape: p.shape, anchor: p.anchor, delay: p.delay, duration: 2.75, tickInterval: 0.5, telegraph: 'none', hit: { shape: p.hit.shape, coef: (9 * 0.7) / 6, ...FIELD } }));
};

const snapFire = (s: ResolvedSkill): void => {
  shiftDelay(-10)(s);
  const st = s.stages[0];
  if (!st) return;
  for (const a of st.actions) a.at = Math.min(a.at, 0.1);
  st.duration = Math.min(st.duration, 0.35);
};

const forkedBeam = (s: ResolvedSkill): void => {
  const h = s.holding;
  const main = h?.tick.find((a): a is Extract<SkillAction, { kind: 'hit' }> => a.kind === 'hit');
  if (!h || !main || main.hit.shape.kind !== 'rect') return;
  const { length, width } = main.hit.shape;
  // The forks split off 2 m out at ±25°, so a lone target is only struck by the main beam.
  const fork = (deg: number) => hit(0, rect(length * 0.8, width, 2, deg), main.hit.coef * 0.5, { reaction: { kind: 'flinch' } });
  h.tick.push(fork(-25), fork(25));
};

const overcharge = (s: ResolvedSkill): void => {
  const h = s.holding;
  if (!h) return;
  h.tickInterval /= 2;
  for (const a of h.tick) if (a.kind === 'hit') a.hit.coef *= 0.75;
  const main = h.tick.find((a): a is Extract<SkillAction, { kind: 'hit' }> => a.kind === 'hit');
  const length = main?.hit.shape.kind === 'rect' ? main.hit.shape.length : 16;
  h.finisher = stage('Shoot', 0.45, [hit(0.1, rect(length, 3), 12, { reaction: { kind: 'knockback', power: 2 }, vfx: 'heavy' })]);
};

const lanceBarrage = (s: ResolvedSkill): void => {
  const lance = mainArea(s)?.area.hit;
  const r = 0.8 * (lance?.shape.kind === 'circle' ? lance.shape.radius / LANCE_RADIUS : 1);
  s.castType = 'normal';
  s.maxRange = undefined;
  s.stages = [
    stage(
      'Shoot',
      0.6,
      Array.from({ length: 6 }, (_, i) => proj(0.12 + i * 0.05, { speed: 32, range: 11, radius: r, pierce: true, hit: { shape: circle(r), coef: 11 / 6, stagger: 'low', reaction: { kind: 'flinch' } } })),
    ),
  ];
};

const swiftVerdict = (s: ResolvedSkill): void => {
  const k = areaScale(s, 5);
  s.castType = 'normal';
  s.castTime = undefined;
  s.maxRange = undefined;
  s.stages = [stage('Slam', 0.55, [hit(0.18, rect(16, 3 * k), 19, { reaction: { kind: 'knockdown', duration: 1.2 }, vfx: 'heavy' })])];
};

const ballLightning = (s: ResolvedSkill): void => {
  const k = areaScale(s, 5);
  const shape = circle(6.5 * k);
  s.stages[0]?.actions.push(area(0.05, { shape, anchor: 'point', delay: 0.6, telegraph: 'none', vfx: 'pillar', hit: { shape, coef: 19 * 0.46, reaction: { kind: 'flinch' } } }));
};

const rollingThunder = (s: ResolvedSkill): void => {
  const k = areaScale(s, 5);
  const shape = circle(5 * k);
  s.stages[0]?.actions.push(area(0.05, { shape, anchor: 'point', delay: 0.6, duration: 4.75, tickInterval: 0.5, telegraph: 'none', hit: { shape, coef: (19 * 0.7) / 10, ...FIELD, reaction: { kind: 'flinch' } } }));
};

const scorchedEarth = (s: ResolvedSkill): void => {
  const meteor = mainArea(s);
  if (!meteor) return;
  const m = meteor.area;
  s.stages[0]?.actions.push(area(meteor.at, { shape: m.shape, anchor: 'point', delay: m.delay, duration: 2.75, tickInterval: 0.5, telegraph: 'none', hit: { shape: m.hit.shape, coef: (22 * 0.55) / 6, ...FIELD } }));
};

const rollingMeteor = (s: ResolvedSkill): void => {
  const meteor = mainArea(s);
  if (!meteor) return;
  const shape = circle(2.2);
  for (let i = 0; i < 8; i++) {
    s.stages[0]?.actions.push(
      area(meteor.at, { shape, anchor: 'point', offset: { x: 0, z: 1.4 * (i + 1) }, delay: (meteor.area.delay ?? 0) + 0.3 * (i + 1), duration: 0.25, tickInterval: 0.1, telegraph: 'none', hit: { shape, coef: (22 * 0.7) / 24, ...FIELD, reaction: { kind: 'flinch' } } }),
    );
  }
};

// ------------------------------------------------------------------ tripods (facets)

const tripods: ClassDef['tripods'] = {
  sw_ember: [
    tripod('sw_ember_t1a', 1, 'Amplify', 'Foes hit take 6% more damage from all sources for 8 s.', T.synergy({ id: 'exposed', duration: 8 })),
    tripod('sw_ember_t1b', 1, 'Kindle', 'Burn deals 50% more damage.', editStatus('burn', (st) => ({ ...st, value: (st.value ?? 0) * 1.5 }))),
    tripod('sw_ember_t1c', 1, 'Frugal', 'Mana cost -67%.', T.mp(-0.67)),
    tripod('sw_ember_t2a', 2, 'Twin Charges', 'Store up to 2 casts.', T.charges(2)),
    tripod('sw_ember_t2b', 2, 'Fire Wall', 'The line is twice as wide and keeps burning for 3 s. Damage +70%.', T.custom(fireWall)),
    tripod('sw_ember_t2c', 2, 'Sweeping Blaze', 'Becomes a point skill: a wall of flame erupts across the target point (up to 12 m), launching foes. Damage +45%.', T.custom(sweepingBlaze), T.dmg(0.45)),
    tripod('sw_ember_t3a', 3, 'Bosskiller', 'Damage +60% to push-immune foes.', T.vsBoss(0.6)),
    tripod('sw_ember_t3b', 3, 'Undying Flame', 'Damage +40% to normal and elite foes; Burn lasts 2 s longer.', T.vsWeak(0.4), editStatus('burn', (st) => ({ ...st, duration: st.duration + 2 }))),
  ],
  sw_orb: [
    tripod('sw_orb_t1a', 1, 'Static', 'Foes hit are Shocked for 4 s.', T.status(SHOCK)),
    tripod('sw_orb_t1b', 1, 'Amplify', 'Foes hit take 6% more damage from all sources for 8 s.', T.synergy({ id: 'exposed', duration: 8 })),
    tripod('sw_orb_t1c', 1, 'Frugal', 'Mana cost -67%.', T.mp(-0.67)),
    tripod('sw_orb_t2a', 2, 'Wide Field', 'Area +30%.', aoeAll(0.3)),
    tripod('sw_orb_t2b', 2, 'Quicken', 'Move speed +20% for 3 s after casting.', T.addAction(self(0.05, { id: 'haste', duration: 3, value: 0.2 }), 0)),
    tripod('sw_orb_t2c', 2, 'Linger', 'The orb lasts 2 s longer.', T.areaDuration(2)),
    tripod('sw_orb_t3a', 3, 'Overload', 'Store up to 2 orbs; each lasts 1 s shorter.', T.charges(2), T.areaDuration(-1)),
    tripod('sw_orb_t3b', 3, 'Storm Spire', 'The orb becomes a 5 m storm spire that pulls hard. Damage +70%.', T.custom(stormSpire), T.dmg(0.7)),
  ],
  sw_nova: [
    tripod('sw_nova_t1a', 1, 'Deep Chill', 'Chill lasts 1 s longer.', editStatus('chill', (st) => ({ ...st, duration: st.duration + 1 }))),
    tripod('sw_nova_t1b', 1, 'Attune', 'Arcana gained +30%.', T.identityGain(0.3)),
    tripod('sw_nova_t1c', 1, 'Quick Cast', 'Cast speed +36%.', T.atkSpeed(0.36)),
    tripod('sw_nova_t2a', 2, 'Focus', 'Damage +60%.', T.dmg(0.6)),
    tripod('sw_nova_t2b', 2, 'Wide Nova', 'Area +20%.', aoeAll(0.2)),
    tripod('sw_nova_t2c', 2, 'Tenacity', 'Immune to push while casting.', T.superArmor('push')),
    tripod('sw_nova_t3a', 3, 'Shatterfrost', 'One second later the frost shatters: foes in the area are frozen for 3 s and take 60% more damage. Cooldown +5 s. No longer counters.', T.custom(shatterfrost), T.cd(5), T.counter(false)),
    tripod('sw_nova_t3b', 3, 'Permafrost Spike', 'Ice spikes erupt all around you, launching foes. Damage +120%. No longer counters.', T.reaction({ kind: 'launch', power: 0.4, duration: 1 }), T.dmg(1.2), T.counter(false)),
  ],
  sw_pillar: [
    tripod('sw_pillar_t1a', 1, 'Kindle', 'Foes hit burn for 5 s.', T.status(BURN(5, 0.25))),
    tripod('sw_pillar_t1b', 1, 'Quick Rise', 'The pillar erupts 0.5 s sooner.', shiftDelay(-0.5)),
    tripod('sw_pillar_t1c', 1, 'Weak Point Focus', 'Weak Point +1.', T.weakPoint(1)),
    tripod('sw_pillar_t2a', 2, 'Ember Pool', 'Leaves a pool of fire for 3 s. Damage +70%.', T.custom(emberPool)),
    tripod('sw_pillar_t2b', 2, 'Bosskiller', 'Damage +30% to push-immune foes.', T.vsBoss(0.3)),
    tripod('sw_pillar_t2c', 2, 'Twin Charges', 'Store up to 2 casts; foes are paralysed instead of launched.', T.charges(2), T.reaction({ kind: 'flinch' })),
    tripod('sw_pillar_t3a', 3, 'Inferno Column', 'A slower, mightier column that erupts 0.4 s later. Damage +95%.', shiftDelay(0.4), T.dmg(0.95)),
    tripod('sw_pillar_t3b', 3, 'Snap Fire', 'The pillar erupts almost instantly. Damage +80%.', T.custom(snapFire), T.dmg(0.8)),
  ],
  sw_arc: [
    tripod('sw_arc_t1a', 1, 'Free Rotation', 'Walk at half speed while channelling; the beam tracks the cursor as you move.', T.custom((s) => {
      if (s.holding) s.holding.moveMult = 0.5;
    })),
    tripod('sw_arc_t1b', 1, 'Quick Prep', 'Cooldown -8 s.', T.cd(-8)),
    tripod('sw_arc_t1c', 1, 'Conduct', 'Foes hit are Shocked for 4 s.', T.status(SHOCK)),
    tripod('sw_arc_t2a', 2, 'Focus', 'Damage +50%.', T.dmg(0.5)),
    tripod('sw_arc_t2b', 2, 'Long Arc', 'Beam length +3 m.', T.range(3)),
    tripod('sw_arc_t2c', 2, 'Precision', 'Crit rate +60%.', T.crit(0.6)),
    tripod('sw_arc_t3a', 3, 'Forked Beam', 'The beam forks into three (the side forks deal half). Damage +120%.', T.custom(forkedBeam), T.dmg(1.2)),
    tripod('sw_arc_t3b', 3, 'Overcharge', 'Pulse twice as fast (10 pulses) and end with a release blast. Total damage +170%.', T.custom(overcharge)),
  ],
  sw_lances: [
    tripod('sw_lances_t1a', 1, 'Chill', 'Foes hit are Chilled for 3 s (attack speed -20%, move speed -40%).', T.status(CHILL(3))),
    tripod('sw_lances_t1b', 1, 'Quick Prep', 'Cooldown -8 s.', T.cd(-8)),
    tripod('sw_lances_t1c', 1, 'Attune', 'Arcana gained +30%.', T.identityGain(0.3)),
    tripod('sw_lances_t2a', 2, 'Pierce', 'Ignore 50% of defence.', T.defPen(0.5)),
    tripod('sw_lances_t2b', 2, 'Wide Volley', 'Area +20%.', aoeAll(0.2)),
    tripod('sw_lances_t2c', 2, 'Tenacity', 'Immune to push while casting.', T.superArmor('push')),
    tripod('sw_lances_t3a', 3, 'Razor Ice', 'Damage +95%.', T.dmg(0.95)),
    tripod('sw_lances_t3b', 3, 'Lance Barrage', 'Becomes a normal skill: fire six piercing lances along an 11 m line. Damage +60%.', T.custom(lanceBarrage), T.dmg(0.6)),
  ],
  sw_thunder: [
    tripod('sw_thunder_t1a', 1, 'Frugal', 'Mana cost -67%.', T.mp(-0.67)),
    tripod('sw_thunder_t1b', 1, 'Wide Crack', 'Area +20%.', aoeAll(0.2)),
    tripod('sw_thunder_t1c', 1, 'Conduct', 'Foes hit are Shocked for 4 s.', T.status(SHOCK)),
    tripod('sw_thunder_t2a', 2, 'Swift Verdict', 'No cast bar: instantly strike a 16 m line toward the cursor. Damage +40%.', T.custom(swiftVerdict), T.dmg(0.4)),
    tripod('sw_thunder_t2b', 2, 'Ball Lightning', 'A second, larger blast (6.5 m) follows. Damage +46%.', T.custom(ballLightning)),
    tripod('sw_thunder_t2c', 2, 'Rolling Thunder', 'Lightning keeps striking the spot for 5 s. Damage +70%.', T.custom(rollingThunder)),
    tripod('sw_thunder_t3a', 3, 'Overflow Surge', 'Damage +90%, and a further +20% while Overflowing.', T.dmg(0.9), addTag('overflowSurge')),
    tripod('sw_thunder_t3b', 3, 'Final Word', 'Crit damage +300%.', T.critDmg(3)),
  ],
  sw_starfall: [
    tripod('sw_starfall_t1a', 1, 'Kindle', 'Foes hit burn for 5 s.', T.status(BURN(5))),
    tripod('sw_starfall_t1b', 1, 'Frugal', 'Mana cost -67%.', T.mp(-0.67)),
    tripod('sw_starfall_t1c', 1, 'Far Cast', 'Range +4 m.', T.range(4)),
    tripod('sw_starfall_t2a', 2, 'Scorched Earth', 'The impact scorches the ground for 3 s. Damage +55%.', T.custom(scorchedEarth)),
    tripod('sw_starfall_t2b', 2, 'Greater Meteor', 'Damage +50%.', T.dmg(0.5)),
    tripod('sw_starfall_t2c', 2, 'Rolling Meteor', 'The meteor rolls on for 2.5 s, striking up to 24 times along its path. Damage +70%.', T.custom(rollingMeteor)),
    tripod('sw_starfall_t3a', 3, 'Overflow Surge', 'Damage +90%, and a further +20% while Overflowing.', T.dmg(0.9), addTag('overflowSurge')),
    tripod('sw_starfall_t3b', 3, 'Final Word', 'Crit damage +300%.', T.critDmg(3)),
  ],
};

// ------------------------------------------------------------------ identity

const ELEMENTAL = new Set(['fire', 'water', 'lightning']);

/** Original values of skills boosted by Overflow, restored when it ends. */
interface BoostSnapshot {
  weakPoint: number | undefined;
  stagger: StaggerTier;
  hits: [HitSpec, number | undefined, StaggerTier | undefined][];
}
const boosted = new WeakMap<ResolvedSkill, BoostSnapshot>();

const boostable = (s: ResolvedSkill): boolean => (s.kind === 'skill' || s.kind === 'awakening') && (s.element === 'fire' || s.element === 'lightning');

/** Fire spells gain Weak Point +1, lightning spells +1 stagger tier. Idempotent. */
function applyBoosts(a: Actor): void {
  for (const s of a.skills.values()) {
    if (boosted.has(s) || !boostable(s)) continue;
    const snap: BoostSnapshot = { weakPoint: s.weakPoint, stagger: s.stagger, hits: [] };
    forEachHit(s, (h) => snap.hits.push([h, h.weakPoint, h.stagger]));
    if (s.element === 'fire') {
      s.weakPoint = (s.weakPoint ?? 0) + 1;
      forEachHit(s, (h) => {
        if (h.weakPoint) h.weakPoint += 1;
      });
    } else {
      s.stagger = stepStagger(s.stagger, 1);
      forEachHit(s, (h) => {
        if (h.stagger && h.stagger !== 'none') h.stagger = stepStagger(h.stagger, 1);
      });
    }
    boosted.set(s, snap);
  }
}

function clearBoosts(a: Actor): void {
  for (const s of a.skills.values()) {
    const snap = boosted.get(s);
    if (!snap) continue;
    s.weakPoint = snap.weakPoint;
    s.stagger = snap.stagger;
    for (const [h, wp, stg] of snap.hits) {
      h.weakPoint = wp;
      h.stagger = stg;
    }
    boosted.delete(s);
  }
}

/** Keeps a gauge-driven buff alive with a timer matching the remaining Arcana. */
function syncBuff(world: World, a: Actor, id: string, remaining: number): void {
  const st = a.getStatus(id);
  if (st) {
    st.remaining = remaining;
    st.duration = Math.max(st.duration, remaining);
  } else {
    applyStatus(world, a, { id, duration: remaining }, a.id, 0);
  }
}

const drainOf = (mode: string): number => (mode === 'torrent' ? TORRENT_DRAIN : DRAIN);

function startOverflow(world: World, a: Actor): void {
  const id = a.player!.identity;
  if (id.mode !== 'idle' || id.gauge < OVERFLOW_MIN) return;
  const torrent = id.gauge >= id.max - 1e-6;
  id.mode = torrent ? 'torrent' : 'overflow';
  // All cooldowns currently running are cut by a quarter.
  for (const [sid, t] of a.cooldowns) {
    if (a.skills.get(sid)?.kind === 'skill') a.cooldowns.set(sid, t * (1 - OVERFLOW_CDR));
  }
  const left = id.gauge / drainOf(id.mode);
  applyStatus(world, a, { id: 'sw_overflow', duration: left }, a.id, 0);
  if (torrent) applyStatus(world, a, { id: 'sw_torrent', duration: left }, a.id, 0);
  applyBoosts(a);
}

function endOverflow(world: World, a: Actor): void {
  const id = a.player!.identity;
  if (id.mode === 'idle') return;
  id.mode = 'idle';
  removeStatus(world, a, 'sw_overflow');
  removeStatus(world, a, 'sw_torrent');
  clearBoosts(a);
}

const gainMult = (s: SkillDef): number => (s as Partial<ResolvedSkill>).identityGainMult ?? 1;

const identity: IdentityController = {
  gaugeMax: 100,
  init(a) {
    const id = a.player!.identity;
    id.gauge = 0;
    id.mode = 'idle';
    id.timer = 0;
    id.data = {};
  },
  onSkillCast(_w, a, s) {
    const id = a.player!.identity;
    if (s.id === phaseStep.id) {
      id.gauge = Math.max(0, id.gauge - PHASE_COST);
      return;
    }
    // Arcana does not build while it is pouring out.
    if (id.mode !== 'idle') return;
    let gain = 0;
    if (s.kind === 'skill') gain = (s.identityGain ?? 0) * (s.castTime && s.castTime > 0 ? CASTING_GAIN : 1) * gainMult(s);
    else if (s.kind === 'basic') gain = BASIC_GAIN;
    if (gain > 0) id.gauge = Math.min(id.max, id.gauge + gain * a.stats.identityGainMult);
  },
  onKey(world, a, key) {
    const id = a.player!.identity;
    if (key === 'Z') {
      if (id.mode !== 'idle') {
        // Stop early and keep the remaining Arcana (shares the 2 s internal cooldown).
        if ((a.cooldowns.get(overflowSkill.id) ?? 0) > 0) return false;
        endOverflow(world, a);
        a.cooldowns.set(overflowSkill.id, INTERNAL_CD);
        world.emit({ type: 'identity', actorId: a.id, op: 'overflowEnd' });
        return true;
      }
      const s = a.skills.get(overflowSkill.id);
      return s ? world.useSkill(a, s, 'Z') : false;
    }
    const s = a.skills.get(phaseStep.id);
    return s ? world.useSkill(a, s, 'X') : false;
  },
  canCast(a, s) {
    const id = a.player!.identity;
    if (s.id === overflowSkill.id) return id.mode === 'idle' && id.gauge >= OVERFLOW_MIN;
    if (s.id === phaseStep.id) return id.gauge >= PHASE_MIN;
    return true;
  },
  op(world, a, op) {
    if (op === 'overflow') startOverflow(world, a);
  },
  update(world, a, dt) {
    const id = a.player!.identity;
    if (id.mode === 'idle') return;
    const drain = drainOf(id.mode);
    id.gauge = Math.max(0, id.gauge - drain * dt);
    if (id.gauge <= 0) {
      endOverflow(world, a);
      return;
    }
    syncBuff(world, a, 'sw_overflow', id.gauge / drain);
    if (id.mode === 'torrent') syncBuff(world, a, 'sw_torrent', id.gauge / drain);
    // Re-applies boosts if the skill table was rebuilt mid-Overflow.
    applyBoosts(a);
  },
  damageMult(a, s, target?: Actor) {
    let m = 1;
    // "vsMobs:2" = +200% against normal and elite monsters (Skyrend Tempest).
    for (const t of s.tags ?? []) {
      const [k, v] = t.split(':');
      if (k === 'vsMobs' && target?.monster && GRADE_RANK[target.monster.grade] <= GRADE_RANK.elite) m *= 1 + (Number(v) || 0);
    }
    if (a.player?.identity.mode === 'idle') return m;
    if (s.element && ELEMENTAL.has(s.element)) m *= 1 + ELEMENTAL_BONUS;
    if (s.tags?.includes('overflowSurge')) m *= 1 + SURGE_BONUS;
    return m;
  },
  describe(a) {
    const id = a.player!.identity;
    const active = id.mode !== 'idle';
    return {
      label: id.mode === 'torrent' ? 'TORRENT' : active ? 'OVERFLOW' : 'Arcana',
      ready: !active && id.gauge >= OVERFLOW_MIN,
      active,
    };
  },
};

export const STORMWEAVER: ClassDef = {
  id: 'stormweaver',
  name: 'Stormweaver',
  title: 'Elemental Battle-Mage',
  archetype: 'Caster with burst window and teleport',
  role: 'dps',
  description: 'A focus-staff mage who layers fire, frost and lightning. Spells build Arcana; pour it out in an Overflow of rapid casting, or spend it to Phase Step across the battlefield.',
  hue: HUE,
  weapon: 'staff',
  model: 'hero_mage',
  baseStats: (level) => classBaseStats(level, { hp: 0.88, mp: 1.2 }),
  skills: [emberLine, stormOrb, frostNova, pillarOfFlame, arcBeam, glacialLances, thunderclap, starfall],
  defaultBar: ['sw_ember', 'sw_orb', 'sw_nova', 'sw_pillar', 'sw_arc', 'sw_lances', 'sw_thunder', 'sw_starfall'],
  basic: basicAttack('sw_basic', 'Arcane Bolts', HUE, {
    coefs: [0.4, 0.4, 0.8],
    ranged: { speed: 26, range: 12, radius: 0.5, homing: 0.35, hit: { shape: circle(0.5), coef: 0.4, reaction: { kind: 'flinch' } } },
  }),
  dash: dashSkill('sw_dash', 'Glimmer Leap', HUE, 8, 5, { teleport: true }),
  awakenings: [skyrendTempest],
  identitySkills: [overflowSkill, phaseStep],
  tripods,
  identity,
  identityName: 'Arcana',
  identityDescription: 'Spells build Arcana (6 / 10 / 14 per cast by tier; cast-bar spells +50%; basic attacks 2). At 30+ press Z to Overflow: Arcana drains 8/s while cast times are halved, current cooldowns drop 25%, elemental spells deal +15%, fire spells gain Weak Point +1 and lightning spells +1 stagger tier. Overflowing from a full gauge unleashes Arcane Torrent instead (+20% crit, drains 5/s). Press Z again to stop early. X is Phase Step: a 12 m blink that needs 30 Arcana and costs 15.',
};
